import React, { useRef, useState } from 'react';
import styles from './DocumentDrawer.module.scss';
import { Drawer, Spin, Modal, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { documentDrawerSelector } from '../../store/selectors/new-document.selector';
import DocumentForm from '../DocumentForm/DocumentForm';
import { actions as newDocumentActions } from '../../store/actions/new-document.actions';
import PreviewPanel from '../PreviewPanel/PreviewPanel';
import RelatedButtons from '../RelatedButtons/RelatedButtons';
import PreviewRelatedObjects from '../PreviewRelatedObjects/PreviewRelatedObjects';
import useCreateDocs from '../../mutation/useCreateDocs';
import useChangeDocument from '../../hooks/useChangeDocument';
import { documentsActions } from '../../store/actions/documents.actions';
import { docTypes, sourceTypes } from '../../constants/documents.constants';
import useDocsAttachmentsQuery from '../../queries/useDocsAttachmentsQuery';
import FooterDoc from '../FooterDoc/FooterDoc';
import HeaderDoc from '../HeaderDoc/HeaderDoc';
import { useVisiblePreview, useSetVisiblePreview } from '../DocFormProvider';

const { confirm } = Modal;

const DocumentDrawer = () => {
  const [visibleRelatedObjects, setVisibleRelatedObjects] = useState(false);
  const [files, setFiles] = useState([]);

  const visiblePreview = useVisiblePreview();
  const setVisiblePreview = useSetVisiblePreview();

  const formRef = useRef();

  const { visible, documentId, formValues, mode, savedDraft } = useSelector(
    documentDrawerSelector
  );

  const { createDocs, isLoading } = useCreateDocs();
  const { changeDocs, isLoadingChangeDoc } = useChangeDocument();

  const { data: docsAttachments } = useDocsAttachmentsQuery(documentId, {
    enabled: Boolean(documentId),
  });

  const accessTypeIds = [
    docTypes.incoming,
    docTypes.outgoing,
    docTypes.citizenAppeal,
  ];

  const showRelatedFilesSwitch =
    (mode === 'edit' &&
      docsAttachments &&
      docsAttachments.length > 0 &&
      docsAttachments) ||
    (mode === 'create' && files && files.length > 0);

  const showRelatedObjectsSwitch =
    mode === 'edit' && accessTypeIds.includes(formValues?.type.id);

  const showRelatedButtons = showRelatedFilesSwitch || showRelatedObjectsSwitch;

  const dispatch = useDispatch();

  const handleFilesChange = (newFiles) => {
    setFiles(newFiles);
  };

  const onClose = () => {
    dispatch(newDocumentActions.openCloseDocument());

    setVisibleRelatedObjects(false);
    setVisiblePreview(null);
    setFiles([]);

    if (formRef.current) {
      formRef.current.resetFields();
    }

    dispatch(documentsActions.fetchDocuments());
  };

  const handleClose = () => {
    if (formRef?.current?.isFieldsTouched()) {
      confirm({
        title: 'Вы уверены, что хотите закрыть окно? ',
        content: 'Введенные Вами данные будут потеряны.',
        zIndex: 20000,
        onOk: () => {
          onClose();
        },
        onCancel: () => {},
      });
      return;
    }

    if (mode === 'edit' && !docsAttachments?.length) {
      message.warning('Пожалуйста прикрепите файлы документа');
      return;
    }

    onClose();
  };

  const handleChangePreview = (value) => {
    if (!!value && visibleRelatedObjects) {
      setVisibleRelatedObjects(false);
    }

    if (typeof value === 'object') {
      setVisiblePreview(value);
      return;
    }
    setVisiblePreview({
      visible: value,
      value: 0,
    });
  };

  const handleRelatedObjects = (value) => {
    if (value && !!visiblePreview?.visible) {
      setVisiblePreview(null);
    }

    setVisibleRelatedObjects(value);
  };

  const showConfirm = () => {
    return new Promise((resolve) => {
      Modal.confirm({
        title: 'Вы уверены, что хотите сохранить изменения? ',
        content:
          'После сохранения документа изменить ряд полей будет невозможно',
        zIndex: 20000,
        onOk: () => {
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
        onClose: () => {
          resolve(false);
        },
      });
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    formRef.current.validateFields(async (err, values) => {
      if (!err) {
        if (mode === 'edit') {
          if (!docsAttachments?.length) {
            message.warning('Пожалуйста прикрепите файлы документа');
            return;
          }

          if (
            formValues.canChange &&
            formValues?.sourceId === sourceTypes.mail
          ) {
            const confirmed = await showConfirm();

            if (!confirmed) return;
          }

          await changeDocs(formValues, values);
        } else {
          await createDocs({ values, documentId: savedDraft?.documentId });
        }

        onClose();
      }
    });
  };

  return (
    <>
      <Drawer
        placement="left"
        title={
          <HeaderDoc
            document={formValues}
            mode={mode}
          />
        }
        visible={visible}
        className={styles.customDrawer}
        width="50%"
        onClose={handleClose}
        destroyOnClose={true}
      >
        <div className={styles.content}>
          {showRelatedButtons && (
            <RelatedButtons
              handleChangePreview={handleChangePreview}
              visibleRelatedObjects={visibleRelatedObjects}
              handleRelatedObjects={handleRelatedObjects}
              showRelatedFilesSwitch={showRelatedFilesSwitch}
              showRelatedObjectsSwitch={showRelatedObjectsSwitch}
            />
          )}
          <Spin spinning={isLoading || isLoadingChangeDoc}>
            <DocumentForm
              wrappedComponentRef={formRef}
              handleFilesChange={handleFilesChange}
              onChangePreview={handleChangePreview}
              visiblePreview={visiblePreview}
            />
          </Spin>
        </div>
        <FooterDoc
          onCancel={handleClose}
          onSubmit={handleSubmit}
          isLoading={isLoading || isLoadingChangeDoc}
        />
      </Drawer>
      {!!visiblePreview?.visible && (
        <PreviewPanel
          files={mode === 'edit' ? docsAttachments : files}
          valueVisible={visiblePreview?.value || 0}
          onChangePreview={handleChangePreview}
        />
      )}
      {visibleRelatedObjects && (
        <PreviewRelatedObjects onClose={setVisibleRelatedObjects} />
      )}
    </>
  );
};

export default DocumentDrawer;
